import * as B from '@blueprintjs/core';
import {useVirtualizer} from '@tanstack/react-virtual';
import * as I from 'immutable';
import React from 'react';
import {TableInstance} from 'react-table';

import {ApiFeature} from 'app/modules/Remote/Feature';
import {
  ApiAlertCountsByFeatureId,
  ApiFeatureCollection,
} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import {setIsImageHidden} from 'app/pages/MonitorProjectView/utils';
import ImageryPurchaseDrawer from 'app/pages/PropertyOverview/ImageryPurchaseDrawer';
import cs from 'app/pages/PropertyOverview/PropertyOverview.module.scss';
import {
  PropertyRow,
  SelectedRowActions,
  TableHeader,
} from 'app/pages/PropertyOverview/PropertyTable';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {FeaturesActions} from 'app/providers/FeaturesProvider';
import {OrderableScenesLoader} from 'app/providers/OrderableScenesProvider';
import {StatusMaybe} from 'app/utils/hookUtils';
import * as routeUtils from 'app/utils/routeUtils';
import {isOwnerOrAdmin} from 'app/utils/userUtils';

interface Props {
  table: TableInstance<any>;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  itemData: Parameters<typeof PropertyRow>[0]['data'];
  gridTemplateColumns: string;
  featuresStatus: any;
  isUpdatingFeatures: boolean;
  hasSelectedPropertyRows: boolean;
  features: I.ImmutableOf<ApiFeature[]>;
  featureIdsForImageryDrawer: I.Set<number>;
  setFeatureIdsForImageryDrawer: (ids: I.Set<number>) => void;
  activeFeatureIdForImageryDrawer: number | null;
  setActiveFeatureIdForImageryDrawer: (id: number | null) => void;
  organization: I.ImmutableOf<ApiOrganization>;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  selectedProject: I.ImmutableOf<ApiProject>;
  selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>;
  organizationUsers: ApiOrganizationUser[];
  projectId: string;
  year: number;
  userNoteCountsByFeatureId: StatusMaybe<any>;
  alertNoteCountsByFeatureId: StatusMaybe<ApiAlertCountsByFeatureId>;
  reportCountsByFeatureId: Record<string, number> | undefined;
  featuresActions: FeaturesActions;
  loggedInUserActions: LoggedInUserActions;
  orderableScenesLoader: OrderableScenesLoader;
  onSetOrderImageryInfo: (info: {feature: any; orderableScene: any}) => void;
  setIsUpdatingFeatures: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCustomizeTagsModalOpen: (open: boolean) => void;
  openTaskImageryModal?: () => void;
}

export const PropertyTableView: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  table,
  tableContainerRef,
  itemData,
  gridTemplateColumns,
  featuresStatus,
  isUpdatingFeatures,
  hasSelectedPropertyRows,
  features,
  featureIdsForImageryDrawer,
  setFeatureIdsForImageryDrawer,
  activeFeatureIdForImageryDrawer,
  organization,
  profile,
  selectedProject,
  selectedFeatureCollection,
  organizationUsers,
  projectId,
  year,
  userNoteCountsByFeatureId,
  alertNoteCountsByFeatureId,
  featuresActions,
  orderableScenesLoader,
  onSetOrderImageryInfo,
  setIsUpdatingFeatures,
  setIsCustomizeTagsModalOpen,
  openTaskImageryModal,
}) => {
  const rowVirtualizer = useVirtualizer({
    count: table.rows.length,
    estimateSize: () => 56,
    getScrollElement: () => tableContainerRef.current,
    getItemKey: React.useMemo(() => (index) => table.rows[index].id, [table.rows]),
  });

  return (
    <div className={cs.tableContainer} ref={tableContainerRef}>
      {featuresStatus === 'fetching' || isUpdatingFeatures ? (
        <B.Spinner className={cs.tableLoadingSpinner} />
      ) : (
        <>
          <table>
            <thead>
              <TableHeader table={table} gridTemplateColumns={gridTemplateColumns} />
              {hasSelectedPropertyRows && !isUpdatingFeatures && (
                <tr className={cs.selectedRowActionsContainer} style={{gridTemplateColumns}}>
                  <th style={{padding: 0, width: '100vw'}}>
                    <SelectedRowActions
                      project={selectedProject}
                      role={profile.get('role')}
                      rows={table.flatRows}
                      organization={organization}
                      userNoteCountsByFeatureId={userNoteCountsByFeatureId}
                      alertNoteCountsByFeatureId={alertNoteCountsByFeatureId}
                      year={year}
                      organizationUsers={organizationUsers}
                      updateFeature={featuresActions.updateFeature}
                      batchPatchFeature={featuresActions.batchPatchFeature}
                      batchArchiveFeatures={featuresActions.batchArchiveFeatures}
                      moveFeatures={featuresActions.moveFeatures}
                      refreshFeatures={featuresActions.refreshFeatures}
                      clearSelection={() => table.toggleAllRowsSelected(false)}
                      selectedFeatureCollection={selectedFeatureCollection}
                      openCustomizeTagsModal={() => setIsCustomizeTagsModalOpen(true)}
                      setIsUpdatingFeatures={setIsUpdatingFeatures}
                      openTaskImageryModal={openTaskImageryModal}
                    />
                  </th>
                </tr>
              )}
            </thead>
            <tbody
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const row = table.rows[virtualRow.index];
                return <PropertyRow key={row.id} data={itemData} virtualRow={virtualRow} />;
              })}
            </tbody>
          </table>
          {table.rows.length === 0 && (
            <div className={cs.emptyListMessage}>
              No properties match these filters.
              <br />
              <a
                onClick={(ev) => {
                  ev.preventDefault();
                  table.setAllFilters([]);
                }}
              >
                Show all properties
              </a>
            </div>
          )}
        </>
      )}

      <ImageryPurchaseDrawer
        organization={organization}
        profile={profile}
        features={features.filter((f) => featureIdsForImageryDrawer.has(f!.get('id'))).toList()}
        year={year}
        orderableScenesLoader={orderableScenesLoader}
        onPreviewOrderableScene={(feature, orderableScene) =>
          onSetOrderImageryInfo({feature, orderableScene})
        }
        onClose={() => setFeatureIdsForImageryDrawer(I.Set())}
        makeSceneUrl={(featureLensId, sourceId, date) =>
          routeUtils.makeHighResSceneUrl({
            organizationId: organization.get('id'),
            projectId,
            featureLensId,
            date,
            sourceId,
          })
        }
        setIsImageHidden={
          isOwnerOrAdmin(profile.get('role'))
            ? (feature, orderableScene, isHidden) => {
                setIsImageHidden(featuresActions.updateFeature, feature, orderableScene, isHidden);
              }
            : null
        }
        activeFeatureId={activeFeatureIdForImageryDrawer ?? undefined}
      />
    </div>
  );
};

export default PropertyTableView;
